import {BLE} from "@awesome-cordova-plugins/ble/ngx";
import {ClaEvents} from "../model/events";
import {Events} from "ionic-angular";
import * as events from "events";
import {LogService} from "../providers/logging/log.service";

export class BleCore {
  Esc = 27;
  Sof = 83;
  Eof = 69;
  DeviceAddress = 1;
  StreamControl = 0;
  ControlStreamMsgId = 0;
  serviceId = '569A1101-B87F-490C-92CB-11BA5EA5167C';
  rxFifoChar = '569A2001-B87F-490C-92CB-11BA5EA5167C';
  txFifoChar = '569A2000-B87F-490C-92CB-11BA5EA5167C';
  processedByte = false;

  public connected: boolean = false;
  private _deviceId: string;
  private _ble: BLE;
  private dataBuffer: number[] = [];
  public TEMPDATABUFFER: number[] = [];

  constructor(
    ble: BLE,
    public events: Events,
    public logger: LogService
  ) {
    this._ble = ble;
  }

  public async SetupDevice(id: string) : Promise<void> {
    if(id == undefined || id == "") {
      return;
    }

    this.logger.info('Connecting', [{"SourceContext": "BleCore", "DeviceId": id, "Function": "SetupDevice"}]);
    await this._ble.connect(id).subscribe(async device => {
      this.logger.info(`Connected to device ${JSON.stringify(device)}`, [{"SourceContext": "BleCore", "DeviceId": id, "Function": "SetupDevice"}]);
      this._deviceId = id;
      if(device) {
        const service = device.services.find(s => s === this.serviceId);
        const characteristics = device.characteristics.find(c => c.characteristic === this.txFifoChar).characteristic
        if(characteristics && service) {
          await this.StartListen(service, characteristics);

          await this.ReadStatus();
          await this.ReadConfig();
        } else {
          this.events.publish(ClaEvents.DEVICE_CONNECTED, false);
        }
      }
      this.events.publish(ClaEvents.DEVICE_CONNECTED, this.connected);
    }, error => {
      this.logger.error(`Error Connecting ${JSON.stringify(error)}`, [{"SourceContext": "BleCore", "DeviceId": id, "Function": "SetupDevice"}]);
      this.events.publish(ClaEvents.DEVICE_CONNECTED, false);
    });
  }

  public async DoListen() {
  }

  public getFloat(data: number[]): number {
    let buffer = new ArrayBuffer(4);
    let bytes = new Uint8Array(buffer);
    bytes[0] = data[0];
    bytes[1] = data[1];
    bytes[2] = data[2];
    bytes[3] = data[3];
    const view = new DataView(buffer);
    return view.getFloat32(0,false);
  }

  public async Disconnect() {
    await this._ble.stopNotification(this._deviceId, this.serviceId, this.txFifoChar).catch(error => {
      this.logger.error(`Stop Notification error`, [{"SourceContext": "BleCore", "Function": "Disconnect"}]);
    });
    await this._ble.disconnect(this._deviceId).catch(error => {
      this.logger.error(`Disconnect error ${JSON.stringify(error)}`, [{"SourceContext": "BleCore", "Function": "Disconnect"}]);
    });
    this.connected = false;
    this.logger.info(`Disconnected`, [{"SourceContext": "BleCore", "Function": "Disconnect"}]);
    this.dataBuffer = [];
    this.TEMPDATABUFFER = [];
    this.processedByte = false;
  }

  private async StartListen(service: string, characteristic: string) {
    this.logger.debug(`Start Listening`, [{"SourceContext": "BleCore", "Function": "StartListen"}]);
    this._ble.startNotification(this._deviceId, service, characteristic).subscribe(async ([buffer]) => {
      let data = new Uint8Array(buffer);

      this.TEMPDATABUFFER.push(...Array.from(data));

      await this.NewCheckForCmds(Array.from(data));
    }, async error => {
      this.logger.error(`Listen error ${JSON.stringify(error)}`, [{"SourceContext": "BleCore", "Function": "StartListen"}]);
    });
  }

  async StreamEnable(stremId: number, enable: boolean) {
    let extraCmds: number[] = [];
    extraCmds.push(stremId);
    extraCmds.push(enable ? 0x01: 0x00);
    await this.SendCommand(CommandDefinitions.CmdStream, extraCmds, true);
  }

  async SendCommand(command: number, extraCmds: number[], enableEscCheck: boolean = true) {
    let extraCmdsString = "";
    if(extraCmds != undefined) {
      extraCmdsString = extraCmds.join(",");
    }

    let msgId: Uint8Array;
    const encoder = new TextEncoder();
    do {
      let msgIdString = this.ControlStreamMsgId < 10 ? '0' : '';
      msgId = encoder.encode(`${msgIdString}${this.ControlStreamMsgId}`);
      this.ControlStreamMsgId++;
    }
    while (msgId.indexOf(this.Esc) !== -1)

    let baseCmd: number[]= [];
    baseCmd.push(this.DeviceAddress, this.StreamControl, command, msgId[0], msgId[1]);

    if(extraCmds != undefined) {
      baseCmd.push(...extraCmds);
    }

    baseCmd.push(this.Checksum(baseCmd));

    // add in ESC ESC if necessary
    if(enableEscCheck && baseCmd.indexOf(this.Esc) !== -1)
    {
      let testCmd: number[] = {...baseCmd}
      for(let i = 0; i < testCmd.length; i++) {
        if(testCmd[i] == this.Esc)
        {
          baseCmd.splice(i, 0, this.Esc);
        }
      }
    }
    // add start of file & end of file
    let cmd = [this.Esc, this.Sof];
    cmd.push(...baseCmd, this.Esc, this.Eof);

    let cmdBytes = new Uint8Array(cmd.length);
    cmdBytes.set(cmd);
    if(cmdBytes.length > 20) {
      for(let i = 0; i < cmdBytes.length; i = i + 20) {
        let cmdPartBuffer = cmdBytes.slice(i,i+20);
        await this._ble.write(this._deviceId, this.serviceId, this.rxFifoChar, cmdPartBuffer.buffer).catch(error => {
          this.logger.error(`WRITE ERROR ${JSON.stringify(error)} in command length greater than 20 ${cmdPartBuffer}`, [{"SourceContext": "BleCore", "Function": "SendCommand"}]);
        });
      }
    } else {
      await this._ble.write(this._deviceId, this.serviceId, this.rxFifoChar, cmdBytes.buffer).catch(error => {
        this.logger.error(`WRITE ERROR ${JSON.stringify(error)} in command ${cmdBytes}`, [{"SourceContext": "BleCore", "Function": "SendCommand"}]);
        return;
      });
    }
  }

  Checksum(cmd: number[]): number {
    let i: number;
    let checkVal = 0;
    for(i = 0; i < cmd.length; i++) {
      checkVal += cmd[i];
    }

    // 2's complement
    checkVal = Math.pow(2, 24) - checkVal;

    // isolate low byte
    checkVal = checkVal & 255;

    return checkVal;
  }

  public async ReadStatus() {
    await this.SendCommand(CommandDefinitions.CmdStatus,[],true);
    this.logger.debug('ReadStatus requested', [{"SourceContext": "BleCore", "Function": "ReadStatus"}]);
  }

  public async ReadConfig() {
    await this.SendCommand(CommandDefinitions.CmdReadConfig, [], true);
    this.logger.debug('ReadConfig requested', [{"SourceContext": "BleCore", "Function": "ReadConfig"}]);
  }

  public ReportPower(power: number, topRange: number, bottomRange: number) {
    const TOPPOWERRANGE = topRange;
    const BOTTOMPOWERRANGE = bottomRange;
    const TOTAL = TOPPOWERRANGE - BOTTOMPOWERRANGE;
    let powerInRange = power - BOTTOMPOWERRANGE;
    let powerPercent = (powerInRange / TOTAL) * 100;
    if(powerPercent > 100) powerPercent = 100;
    // connect to event
    this.events.publish(ClaEvents.POWER_LEVEL, powerPercent);
  }

  // This will run with the data from a new packet each time it is received
  public async NewCheckForCmds(incomingData: number[]) {

    // 27,27,27,27
    // copy incoming data to new array
    let dataToCheck = [...incomingData];

    // before we begin, we need to see if we have any existing data in the data buffer
    // this is to handle messages split across packets
    // the easiest way to handle this will be to just take the last item off the data buffer, and prepend it
    // to the data to check
    if (this.dataBuffer.length > 0) {
      if (!this.processedByte) {
        dataToCheck = [this.dataBuffer.pop(), ...dataToCheck];
      }
    }

    // loop each individual byte that was received
    for (let i = 0; i < dataToCheck.length; i++) {
      // if the byte is not an escape character, OR it is the last byte in the packet
      // it just goes into the data buffer
      if (dataToCheck[i] !== this.Esc || i === dataToCheck.length - 1) {

        // add byte to dataBuffer
        this.dataBuffer.push(dataToCheck[i]);
        // continue to next byte
        this.processedByte = false;
        continue;
      }

      // if we made it here, we have found a esc character at position i

      // we will look at one of three cases:
      // i + 1 contains another Esc, which is an escape in data
      // i + 1 contains Eoh, which means we are done
      // i + 1 contains Soh, which means we are starting a new packet?
      // i + 1 is something else, which happens if the previous packet carried over an already parsed esc

      // Check if there's a next byte to look at
      if (i + 1 >= dataToCheck.length) {
        // This ESC is the last byte, treat it as data for now (it might be part of a sequence completed in the next chunk)
        this.dataBuffer.push(dataToCheck[i]);
        this.processedByte = false;
        continue;
      }

      // pull out the special character
      const escapedChar = dataToCheck[i + 1];

      // start with esc in data
      if (escapedChar === this.Esc) {
        // add a SINGLE value to the dataBuffer and continue
        this.dataBuffer.push(dataToCheck[i++]);
        this.processedByte = true;
        // continue on with loop
        continue;
      }

      // Next will be a Eoh
      if (escapedChar === this.Eof) {
        // do not add anything to data buffer, and increment i by one to skip the Eof character
        i++;
        this.processedByte = true;
        // take the contents of dataBuffer out
        const msgToHandle = [...this.dataBuffer];

        // clear out databuffer
        this.dataBuffer = [];

        // handle message
        // Only proceed if the message isn't empty (could happen with stray EOPs)
        if (msgToHandle.length > 0) {
          await this.DataHandler(msgToHandle);
          this.logger.debug(`EOF found with data: ${msgToHandle}`, [{"SourceContext": "BleCore", "Function": "NewCheckForCmds"}]);
        } else {
          this.logger.warn(`EOF found with no message`, [{"SourceContext": "BleCore", "Function": "NewCheckForCmds"}]);
        }
        this.TEMPDATABUFFER = [];
        // continue onwards
        continue;
      }

      // Last is Sof
      // Sof should only occur if databuffer is empty
      // so all we have to do is verify that databuffer is empty
      // clear and warn if not
      // and skip a byte
      if (escapedChar === this.Sof) {
        // check for error case - Sof should ONLY occur when dataBuffer is empty
        if (this.dataBuffer.length > 0) {
          // if there is existing data, something went wrong! Warn
          this.logger.warn(`SoH bytes found with data in buffer already! Soh found at pos ${i}. Clearing data buffer before continuing...`, [{"SourceContext": "BleCore", "Function": "NewCheckForCmds"}]);
          this.logger.debug(`DataBuffer: ${this.dataBuffer}`, [{"SourceContext": "BleCore", "Function": "NewCheckForCmds"}]);
          this.logger.debug(`DataToCheck: ${dataToCheck}`, [{"SourceContext": "BleCore", "Function": "NewCheckForCmds"}]);
          this.logger.debug(`incomingData: ${incomingData}`, [{"SourceContext": "BleCore", "Function": "NewCheckForCmds"}]);
          this.logger.debug(`TEMPDATABUFFER: ${this.TEMPDATABUFFER}`, [{"SourceContext": "BleCore", "Function": "NewCheckForCmds"}]);

          // fix any issues by wiping out databuffer
          this.dataBuffer = [];
          this.TEMPDATABUFFER = [];
        }

        // do not add anything to data buffer, and increment i by one to skip the Sof character
        i++;
        this.processedByte = true;
        // continue the loop
        continue;
      }

      // special edge case:
      // if the previous packet ends in ESC+ESC, it will be reduced to a SINGLE ESC, which may come in on this packet
      // if so, we want to just add it to the data buffer and continue
      this.dataBuffer.push(dataToCheck[i]);
      this.processedByte = false;
      this.logger.debug(`Adding to dataBuffer fall through byte ${dataToCheck[i]}`, [{"SourceContext": "BleCore", "Function": "NewCheckForCmds"}]);
    }
  }

  public async DataHandler(dataResponse: number[]) {
    const ResponseConvertValue = 128;
    let responseId = dataResponse[3];
    let dataBuffer: Uint8Array = Uint8Array.from(dataResponse);
    if(dataResponse[1] == this.StreamControl) {
      if(this.ControlStreamMsgId === responseId) {
      }

      if(dataResponse[2] == CommandDefinitions.CmdStatus + ResponseConvertValue) {
        await this.ProcessStatus(dataBuffer.buffer);
      } else if(dataResponse[2] == CommandDefinitions.CmdReadConfig + ResponseConvertValue) {
        await this.ProcessConfig(dataBuffer.buffer);
      }
    } else {
      await this.ProcessData(dataBuffer.buffer);
    }
  }

  async ProcessStatus(data: ArrayBuffer) {
    this.logger.error('ProcessStatus Requires Override', [{"SourceContext": "BleCore", "Function": "ProcessStatus"}]);
  }
  async ProcessConfig(data: ArrayBuffer) {
    this.logger.error('ProcessConfig Requires Override', [{"SourceContext": "BleCore", "Function": "ProcessConfig"}]);
  }
  async ProcessData(data: ArrayBuffer) {
    this.logger.error('ProcessData Requires Override', [{"SourceContext": "BleCore", "Function": "ProcessData"}]);
  }

  // padStart shim
  public padStart(str: string, targetLength: number, padString: string): string {
    targetLength = Math.floor(targetLength) || 0;
    if(targetLength < str.length) return String(str);

    padString = padString ? String(padString) : " ";

    let pad = "";
    const len = targetLength - str.length;
    let i = 0;
    while(pad.length < len) {
      if(!padString[i]) {
        i = 0;
      }
      pad += padString[i];
      i++;
    }

    return pad + String(str).slice(0);
  }
}

export enum CommandReturnStatus {
  CmdStatusSuccess,
  CmdStatusBadCmd,
  CmdStatusBadParam,
  CmdStatusCmdFail
}

export enum CommandDefinitions
{
  CmdId = 0x00,
  CmdMconfig = 0x01,
  CmdStream = 0x02,
  CmdStatus = 0x03,
  CmdReadConfig = 0x04,
  CmdWriteConfig = 0x05,
  CmdWriteProgram = 0x06,
  CmdSetPc = 0x07,
  CmdReadFlash = 0x10,
  CmdWriteFlash = 0x11,
  CmdFakeData = 0x12,
  CmdHpData = 0x15,
  CmdAutoscan = 0x16,
  CmdFwLoad = 0x40
}



// WEBPACK FOOTER //
// ./src/instruments/BleCore.ts